"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Simplify } from "@/prismicio-types";
import { Content } from "@prismicio/client";
import { PrismicNextImage } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";
import React, { useState } from "react";
import customPageLeftShape from "@/assets/common/customPageLeftShape.svg";
import Image from "next/image";
import noise from "@/assets/noise.png";
import { motion } from "framer-motion";

type Props = {
  data: Simplify<Content.EmergencyImmediateHelpSliceDefaultPrimaryEmergencyHelpCardsItem>;
  index: number;
};

export default function EmergencyHelpCard({ data, index }: Props) {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  return (
    <motion.div
      className="h-full"
      onHoverStart={() => {
        setHoveredCard(index);
      }}
      onHoverEnd={() => {
        setHoveredCard(null);
      }}
    >
      <motion.div
        className="relative h-full transform-3d perspective-[1000px]"
        animate={{
          rotateY: hoveredCard === index ? 180 : 0,
        }}
        transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="rounded-[10px] px-9 py-10.5 h-full bg-white [box-shadow:0_10px_16px_0_rgba(7,152,242,0.10)] backface-hidden overflow-hidden relative">
          {/* overlay */}
          <Image
            src={customPageLeftShape.src}
            alt="left shape"
            width={710.44}
            height={659.7}
            className="absolute top-0 left-1/2 -translate-x-1/2 h-[350px] w-auto pointer-events-none"
          />

          <div
            className="absolute inset-0 h-full w-full mix-blend-overlay opacity-[0.3] pointer-events-none"
            style={{
              background: `url(${noise.src}) lightgray 0% 0% / 60.90909242630005px 60.90909242630005px repeat`,
            }}
          />
          <div className="relative z-10">
            <PrismicNextImage
              field={data.icon}
              alt=""
              className="mb-8 pointer-events-none"
            />
            <h3 className="font-archivo text-3xl font-normal leading-10 mb-3 text-[#033D61]">
              {data.heading}
            </h3>
            <PrismicRichText
              field={data.sub_heading}
              components={{
                paragraph: ({ children }) => (
                  <p className="font-merriweather text-lg font-normal leading-7 text-[#033D61]">
                    {children}
                  </p>
                ),
              }}
            />
          </div>
        </div>

        <div className="absolute inset-0 backface-hidden rotate-y-180 overflow-hidden rounded-[10px]">
          <div className="px-9 py-10.5 h-full relative bg-[#045B91] [box-shadow:0_16px_10px_0_rgba(7,152,242,0.10)] group">
            <div
              className="absolute inset-0 h-full w-full mix-blend-overlay opacity-[0.4] pointer-events-none"
              style={{
                background: `url(${noise.src}) lightgray 0% 0% / 60.90909242630005px 60.90909242630005px repeat`,
              }}
            />
            <div className="relative z-10">
              <PrismicNextImage
                field={data.icon}
                alt=""
                className="brightness-0 invert transition-all mb-8 pointer-events-none"
              />
              <h3 className="font-archivo text-3xl font-normal leading-10 mb-3 text-[#EBF7FF]">
                {data.heading}
              </h3>
              <PrismicRichText
                field={data.sub_heading}
                components={{
                  paragraph: ({ children }) => (
                    <p className="font-merriweather text-lg font-normal leading-7 text-[#EBF7FF]">
                      {children}
                    </p>
                  ),
                }}
              />
            </div>

            <Image
              src={customPageLeftShape.src}
              alt="left shape"
              width={710.44}
              height={659.7}
              className="absolute top-0 left-1/2 -translate-x-1/2 h-[350px] w-auto pointer-events-none"
            />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
